/**
 * Onboarding Tour — Impact Core
 *
 * Uses react-joyride to walk new users through key UI areas.
 * Steps target data-tour attributes placed on key components.
 */

import Joyride, { type CallBackProps, STATUS, type Step } from 'react-joyride';
import { useTranslation } from '@/i18n';

interface OnboardingTourProps {
  run: boolean;
  onComplete: () => void;
}

export function OnboardingTour({ run, onComplete }: OnboardingTourProps) {
  const { t } = useTranslation();

  const steps: Step[] = [
    {
      target: '[data-tour="sidebar"]',
      title: t('tour.sidebarTitle'),
      content: t('tour.sidebarContent'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="dashboard-kpis"]',
      title: t('tour.kpisTitle'),
      content: t('tour.kpisContent'),
      placement: 'bottom',
    },
    {
      target: '[data-tour="quick-actions"]',
      title: t('tour.actionsTitle'),
      content: t('tour.actionsContent'),
      placement: 'bottom',
    },
    {
      target: '[data-tour="language-selector"]',
      title: t('tour.languageTitle'),
      content: t('tour.languageContent'),
      placement: 'bottom',
    },
    {
      target: '[data-tour="ai-chat-bubble"]',
      title: t('tour.aiTitle'),
      content: t('tour.aiContent'),
      placement: 'left',
    },
  ];

  const handleCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      onComplete();
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: '#2563EB',
          zIndex: 10000,
          arrowColor: '#111827',
          backgroundColor: '#111827',
          textColor: '#F0F2F5',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
        },
        tooltip: {
          borderRadius: '12px',
          fontSize: '13px',
        },
        buttonNext: {
          borderRadius: '6px',
          fontSize: '13px',
          padding: '8px 16px',
        },
        buttonBack: {
          color: '#8E99AB',
          fontSize: '13px',
        },
        buttonSkip: {
          color: '#8E99AB',
          fontSize: '13px',
        },
      }}
      locale={{
        back: t('tour.prev'),
        close: t('tour.finish'),
        last: t('tour.finish'),
        next: t('tour.next'),
        skip: t('tour.skip'),
      }}
    />
  );
}
